import React, { useState, useCallback, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { useTranslator } from '@jutro/locale';
import { ModalNext, ModalHeader, ModalBody, ModalFooter } from '@jutro/components';
import { ServiceManager } from '@jutro/legacy/services';
import { useValidation } from '@xengage/gw-portals-validation-react';
import { ViewModelUtil } from '@xengage/gw-portals-viewmodel-js';
import { useDataRefresh } from '@xengage/gw-portals-viewmodel-react';
import { Button } from '@jutro/legacy/components';
import generateSubComponents from './ScheduleItemTypes/ScheduleItemTypes';
import messages from './ScheduleItemsComponentExt.messages';




function ScheduleItemModalPopover(props) {
    const {
        propertyInfo,
        viewModelService,
        scheduleData,
        scheduleItem,
        addAnother,
        labelPosition,
        id,
        isNew,
        isOpen,
        onResolve,
        onReject,
        bodyAutoFocus,
        phoneWide
    } = props;
    const translator = useTranslator();
    const { refreshData } = useDataRefresh();
    const localeService = ServiceManager.getService('locale-service');
    const [modalData, setModalData] = useState(scheduleData);
    const [disable,setdisable]=useState(false)
    const [showError,setshowError]=useState(false)
    const { onValidate } = useValidation(id);

    const modalOverrides = {
        '@field': {
            labelPosition,
            phoneWide,
            showOptional: true
        }
    };

    const stringsIsBlank = (field) => {
        return (field.hasOwnProperty('stringValue') && field.stringValue.trim() === '') 
    }

    const checkValidity=useCallback(()=>{
        propertyInfo.forEach((item)=>{
            if(item.required===true && !_.isEmpty(modalData)){
                if(modalData.itemData.hasOwnProperty(item.id) && !stringsIsBlank(modalData.itemData[item.id])){
                    setdisable(false)
                }
                else{
                    setdisable(true)
                }
            }   
        })
        refreshData()
    },[modalData, propertyInfo, refreshData])

    const checkErrorValidity=useCallback(()=>{
        if(_.isEmpty(modalData)){
            setshowError(true)

            return true
        }

        for(const item of propertyInfo){
            if(item.required===true && !_.isEmpty(modalData)){
                if(!modalData.itemData.hasOwnProperty(item.id) || stringsIsBlank(modalData.itemData[item.id])){
                    setshowError(true)

                    return true
                }

                setshowError(false)
            }

            if(item.id === 'OwnershipPct' && !_.isEmpty(modalData) && !_.isUndefined(modalData.itemData.OwnershipPct) && !_.isUndefined(modalData.itemData.OwnershipPct.integerValue)){
                if(modalData.itemData.OwnershipPct.integerValue < 0 || modalData.itemData.OwnershipPct.integerValue > 100){
                    setshowError(true)

                    return true
                }

                setshowError(false)
            }
        }

        refreshData()

        return false
    },[modalData, propertyInfo, refreshData])

    const handleChange = useCallback(
        (value, path) => {
            const clonedData = _.cloneDeep(modalData);
            const newPath = ViewModelUtil.getNonVMPath(path);

            _.set(clonedData, newPath, value);
            setModalData(clonedData);
            checkValidity();
        },
        [checkValidity, modalData]
    );

    useEffect(()=>{
        scheduleItem.propertyInfos.forEach((element) => {
            if(!_.isEmpty(element.defaultValue)){
                if (element.valueType === "STRING" && _.isEmpty(_.get(modalData, `itemData.${element.id}.stringValue`))){
                    if(element.id==='AddressLine1'){
                         _.set(modalData, `itemData.${element.id}.stringValue`,element.defaultValue.stringValue)
                    }
                    else if(element.id==='ZipCode'){
                        _.set(modalData, `itemData.${element.id}.stringValue`,element.defaultValue.stringValue)
                    }
                    else if(element.id==='City'){
                        _.set(modalData, `itemData.${element.id}.stringValue`,element.defaultValue.stringValue)
                    }
                }
                else if (element.valueType === "TYPEKEY" && _.isEmpty(_.get(modalData, `itemData.${element.id}.typeCodeValue`))){
                    if(element.id==='State'){
                        _.set(modalData, `itemData.${element.id}.typeCodeValue`,element.defaultValue.typeCodeValue)
                    }
                    else if(element.id==='AddressType'){
                        _.set(modalData, `itemData.${element.id}.typeCodeValue`,element.defaultValue.typeCodeValue)
                    }
                    else if(element.id==='RelationshipTitle'){
                        _.set(modalData, `itemData.${element.id}.typeCodeValue`,element.defaultValue.typeCodeValue)
                    }
                }
            }
        });
    },[])
    
    const orderedPropertyInfo = useMemo(() => _.sortBy(propertyInfo, 'order'), [propertyInfo]);
    const defaultCountryCode = localeService.getDefaultCountryCode();
    const subComponents = orderedPropertyInfo.flatMap((info) => generateSubComponents({
        info,
        modalData,
        viewModelService,
        translator,
        defaultCountryCode,
        scheduleItem,
        onValueChange: handleChange,
        onValidate,
        parentOverrides: modalOverrides,
        isNew,
        showErrors:showError
    }));

    const handleAdd = useCallback((event) => {
        const err = checkErrorValidity();

        modalData.buttonId = event.currentTarget.id

        if(!disable && !err){
            onResolve(modalData)
        }
    }, [checkErrorValidity, disable, modalData, onResolve]);

    const modalOkLabel = useMemo(() => isNew ? messages.scheduleAdd : messages.save, [isNew]);

    return (
        <ModalNext
            isOpen={isOpen}
        >
            <ModalHeader
                title={scheduleItem.displayName}
            />
            <ModalBody
                contentLayout={{
                    component: 'grid'
                }}
                autoFocus={bodyAutoFocus}
            >
                {subComponents}
            </ModalBody>
            <ModalFooter>
                <Button onClick={onReject} type="outlined">
                    {messages.scheduleCancel}
                </Button>
                {addAnother !== undefined && addAnother ? (
                    <Button id = "addAnother" onClick={handleAdd} >
                        {messages.scheduleAddAnother}
                    </Button>
                ) : "" }
                <Button id = "add" onClick={handleAdd} >
                    {addAnother !== undefined && addAnother ? messages.scheduleDone : modalOkLabel}
                </Button>
            </ModalFooter>
        </ModalNext>
    );
}

ScheduleItemModalPopover.propTypes = {
    propertyInfo: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    scheduleData: PropTypes.shape({}),
    phoneWide: PropTypes.shape({}),
    labelPosition: PropTypes.string,
    id: PropTypes.string.isRequired,
    isOpen: PropTypes.string.isRequired,
    onResolve: PropTypes.func,
    scheduleItem: PropTypes.shape({
        displayName: PropTypes.string
    }).isRequired,
    viewModelService: PropTypes.shape({}).isRequired,
    isNew: PropTypes.bool.isRequired,
    bodyAutoFocus: PropTypes.string.isRequired,
    onReject: PropTypes.func.isRequired
};
ScheduleItemModalPopover.defaultProps = {
    scheduleData: {},
    labelPosition: 'left',
    onResolve: undefined,
    phoneWide: {
        labelPosition: 'top'
    },
};
export default ScheduleItemModalPopover;
